/**
 * A CoreMirror view extension providing structural highlighting using
 * CodeMirror's syntax tree.
 *
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { indentUnit } from "@codemirror/language";
import { EditorView, ViewPlugin, ViewUpdate } from "@codemirror/view";
import { lintState } from "../lint/lint";
import { codeBlocks } from "./blocks";
import { overlapsUnnecessaryCode, skipBodyTrailers } from "./doc-util";
import { Positions, VisualBlock } from "./visual-block";

class Measure {
  constructor(
    readonly width: number,
    readonly left: number,
    readonly blocks: VisualBlock[]
  ) {}
  eq(other: Measure) {
    if (this.width !== other.width) {
      return false;
    }
    if (this.left !== other.left) {
      return false;
    }
    const blocksChanged =
      other.blocks.length !== this.blocks.length ||
      other.blocks.some((b, i) => !b.eq(this.blocks[i]));
    if (blocksChanged) {
      return false;
    }
    return true;
  }
}

export const codeStructureView = (option: "full" | "simple") =>
  ViewPlugin.fromClass(
    class {
      measureReq: { read: () => Measure; write: (value: Measure) => void };
      overlayLayer: HTMLElement;
      lastMeasure: Measure = new Measure(0, 0, []);

      constructor(readonly view: EditorView) {
        this.measureReq = {
          read: this.readBlocks.bind(this),
          write: this.drawBlocks.bind(this),
        };
        this.overlayLayer = view.scrollDOM.appendChild(
          document.createElement("div")
        );
        this.overlayLayer.className = "cm-cs--layer";
        this.overlayLayer.classList.add("cm-cs--mode-" + option);
        this.overlayLayer.setAttribute("aria-hidden", "true");
        view.requestMeasure(this.measureReq);
      }

      update(_update: ViewUpdate) {
        // We can probably limit this but we need to know when the language state has changed as parsing has occurred.
        this.view.requestMeasure(this.measureReq);
      }

      readBlocks(): Measure {
        const view = this.view;
        const { state } = view;

        const contentDOMRect = view.contentDOM.getBoundingClientRect();
        // The gutter is awkward as its position fixed in the scroller.
        const gutterWidth =
          view.scrollDOM.firstElementChild!.getBoundingClientRect().width;
        const width = contentDOMRect.width;

        let cursorFound = false;

        /**
         * Calculate visual positions for a node from start/end.
         *
         * @param view The view.
         * @param start The start position.
         * @param end The end position.
         * @param depth Current indent depth (1 per indent level starting at 0).
         * @param parent The parent positions (e.g. for the while block) if we're calculating body positions, otherwise undefined.
         * @returns The positions for the block denoted by start/end or undefined if highlighting should be skipped.
         */
        const positionsForNode = (
          view: EditorView,
          start: number,
          end: number,
          depth: number,
          parent: Positions | undefined
        ): Positions | undefined => {
          const diagnostics = state.field(lintState, false)?.diagnostics;
          const indentWidth =
            state.facet(indentUnit).length * view.defaultCharacterWidth;
          let topLine = view.lineBlockAt(start);
          if (parent) {
            if (topLine.to + 1 < view.state.doc.length) {
              topLine = view.lineBlockAt(topLine.to + 1);
            } else {
              // There's no next line.
              return undefined;
            }
            if (parent.top === topLine.top) {
              // It's the same line, e.g. if True: pass
              return undefined;
            }
          }

          if (overlapsUnnecessaryCode(diagnostics, topLine.from, topLine.to)) {
            return undefined;
          }

          const topLineNumber = state.doc.lineAt(topLine.from).number;
          const bottomPos = skipBodyTrailers(
            state,
            diagnostics,
            end - 1,
            topLineNumber
          );
          if (bottomPos === undefined) {
            // Not sure if this is possible in practice due to the grammar,
            // but best to bail if we encounter it in error scenarios.
            return undefined;
          }
          const bottomLine = view.lineBlockAt(bottomPos);
          const top = topLine.top;
          const bottom = bottomLine.bottom;
          const height = bottom - top;
          const leftIndent = depth * indentWidth;
          const left = leftIndent;
          const mainCursor = state.selection.main.head;
          const cursorActive =
            !cursorFound &&
            mainCursor >= topLine.from &&
            mainCursor <= bottomLine.to;
          if (cursorActive) {
            cursorFound = true;
          }
          return new Positions(top, left, height, cursorActive);
        };

        const bodyPullBack = option === "full";
        const blocks: VisualBlock[] = [];
        // Innermost first, as positionsForNode's cursor attribution relies on.
        for (const block of codeBlocks(state)) {
          // Draw an l-shape for each header/body run.
          const parentPositions = positionsForNode(
            view,
            block.start,
            block.bodyStart,
            block.depth,
            undefined
          );
          const bodyPositions = positionsForNode(
            view,
            block.bodyStart,
            block.bodyEnd,
            block.depth + 1,
            parentPositions
          );
          if (parentPositions && bodyPositions) {
            blocks.push(
              new VisualBlock(
                bodyPullBack,
                width,
                parentPositions,
                bodyPositions
              )
            );
          }
        }
        return new Measure(width, gutterWidth, blocks.reverse());
      }

      drawBlocks(measure: Measure) {
        if (!measure.eq(this.lastMeasure)) {
          this.lastMeasure = measure;
          const { blocks, left, width } = measure;

          this.overlayLayer.style.width = width + "px";
          this.overlayLayer.style.left = left + "px";

          // Should be able to adjust old elements here if it's a performance win.
          this.overlayLayer.textContent = "";
          for (const b of blocks) {
            for (const e of b.draw()) {
              this.overlayLayer.appendChild(e);
            }
          }
        }
      }

      destroy() {
        this.overlayLayer.remove();
      }
    }
  );
